import {
  Box,
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { ChangeEvent, useCallback, useState } from 'react';

import { validateName } from 'utils/formValidator';

import type { ScheduleModalProps } from '../../openingHours.view';

const nameField = 'name';

type Errors = {
  [nameField]?: string;
};

export type EditModalSubmitProps = {
  name: string;
};

type Props = {
  confirmName?: string;
  isLoading?: boolean;
  name: string;
  title: string;
  onClose: ScheduleModalProps['onClose'];
  onSubmit: (arg: EditModalSubmitProps) => void;
};

export const EditModal = ({
  confirmName,
  isLoading,
  name: propName,
  title,
  onClose,
  onSubmit,
}: Props): JSX.Element => {
  const [name, setName] = useState<string>(propName);
  const [errors, setErrors] = useState<Errors>({});

  const validate = useCallback(() => {
    const errors: Errors = {};
    const nameError = validateName(name);

    if (nameError) {
      errors[nameField] = nameError;
    }

    setErrors(errors);

    return Object.keys(errors).length === 0;
  }, [name]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;

    onSubmit({ name });
  }, [name, onSubmit, validate]);

  const handleNameChange = useCallback(
    (event?: ChangeEvent<HTMLInputElement>): void => {
      if (!event) return;

      setName(event.target.value);
    },
    [],
  );

  return (
    <Modal isOpen onClose={onClose}>
      <Box width="456px">
        <form onSubmit={handleSubmit}>
          <ModalHead title={title} onClose={onClose} />
          <ModalBody>
            <FieldText
              label="Name"
              placeholder="e.g. 'Italy' or 'HR Department'"
              required
              input={{
                name: nameField,
                onChange: handleNameChange,
                value: name,
              }}
              meta={{ error: errors[nameField] }}
            />
          </ModalBody>
          <ModalFoot
            cancelOnClick={onClose}
            confirmProps={{ disabled: name === propName, isLoading }}
            confirmName={confirmName || 'Update'}
            confirmOnClick={handleSubmit}
          />
        </form>
      </Box>
    </Modal>
  );
};
